import React, { useState, useEffect } from "react";
// import { Upload, X } from "lucide-react";
import UploadIcon from "../../../assets/uploadIcon.svg";

const AddCardDetailsModal = ({ isOpen, onClose, onSubmit, editData }) => {
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    file: null,
  });

  // Error state
  const [errors, setErrors] = useState({
    title: "",
    description: "",
    file: "",
  });

  // File preview state
  const [filePreview, setFilePreview] = useState("");

  // Reset form when modal opens
  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || "",
        description: editData.description || "",
        file: null
      });
    } else if (isOpen) {
      setFormData({ title: "", description: "", file: null });
      setErrors({ title: "", description: "", file: "" });
      setFilePreview("");
    }
  }, [isOpen, editData]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({
          ...prev,
          file: "Please upload an image file",
        }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          file: "File size should be less than 5MB",
        }));
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setFilePreview(previewUrl);

      setFormData((prev) => ({
        ...prev,
        file,
      }));
      setErrors((prev) => ({
        ...prev,
        file: "",
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};
  
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
      isValid = false;
    }
  
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    } else if (formData.description.length > 500) {
      newErrors.description = "Description cannot exceed 500 characters";
      isValid = false;
    }
  
    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit?.(formData);
      onClose();
    }
  };

  // Handle close
  const handleClose = () => {
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }
    onClose();
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
          >
            {/* <X className="h-5 w-5" /> */}
          </button>

          <h2 className="text-xl font-medium mb-6">Add Card details</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Heading</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="type here"
                className={`w-full p-2 border rounded-md ${
                  errors.title ? "border-red-500" : ""
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Body Text
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Type your reason here"
                className={`w-full p-2 border rounded-md min-h-[100px] resize-none ${
                  errors.description ? "border-red-500" : ""
                }`}
              />
              <div className="flex justify-between text-sm">
                {errors.description && (
                  <p className="text-red-500">{errors.description}</p>
                )}
                <span
                  className={`text-right ${
                    formData.description.length > 500
                      ? "text-red-500"
                      : "text-gray-500"
                  }`}
                >
                  {formData.description.length}/500
                </span>
              </div>
            </div>

            {/* <div>
              <label className="block text-sm font-medium mb-1">
                Upload Icon
              </label>
              <label className="border rounded-md p-2 flex justify-between items-center cursor-pointer hover:bg-gray-50">
                <span className="text-gray-500">
                  {formData.file ? formData.file.name : "Add File"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <img src={UploadIcon} alt="upload-icon" />
                {/* <Upload className="h-5 w-5 text-gray-500" /> 
              </label>
              {errors.file && (
                <p className="text-red-500 text-sm mt-1">{errors.file}</p>
              )}
              {filePreview && (
                <div className="mt-2">
                  <img
                    src={filePreview}
                    alt="Preview"
                    className="h-20 w-20 object-cover rounded"
                  />
                </div>
              )}
            </div> */}
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={handleClose}
              className="flex-1 py-2 px-4 border rounded-md text-blue-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-2 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCardDetailsModal;
